<template>
  <div class="kassa-30days">
    <div class="header-main">
      <div class="hm-left">
        <div class="header-main__title-wrap">
          <h1 class="header-main__title">Кассы за 30 дней</h1>
        </div>
      </div>
    </div>

    <div class="controls">
      <button class="btn-load" :disabled="loading" @click="load(true)">
        {{ loading ? 'Загрузка...' : 'Обновить' }}
      </button>
      <span v-if="periodLabel" class="period-label">{{ periodLabel }}</span>
      <span v-if="rows.length" class="rows-count">Касс: {{ rows.length }}</span>
      <p v-if="error" class="error-msg">{{ error }}</p>
    </div>

    <div v-if="loaded && !rows.length && !loading" class="empty-msg">
      За последние {{ days }} дней касс не найдено.
    </div>

    <div v-if="rows.length" class="table-wrap">
      <table class="report-table">
        <thead>
          <tr>
            <th class="col-check">
              <input
                type="checkbox"
                :checked="allSelected"
                :indeterminate.prop="someSelected && !allSelected"
                title="Выбрать все"
                @change="toggleAll($event.target.checked)"
              />
            </th>
            <th>Число кассы</th>
            <th class="col-num">Наличными</th>
            <th class="col-num">Общая сумма</th>
            <th class="col-num">Без денег</th>
            <th class="col-num">Заказов</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.id"
            :class="{ selected: isSelected(row.id) }"
            @click="toggleRow(row.id)"
          >
            <td class="col-check" @click.stop>
              <input
                type="checkbox"
                :checked="isSelected(row.id)"
                @change="toggleRow(row.id)"
              />
            </td>
            <td class="cell-label">{{ row.label }}</td>
            <td class="col-num">{{ formatMoney(row.cash) }}</td>
            <td class="col-num">{{ formatMoney(row.total) }}</td>
            <td class="col-num">{{ formatMoney(row.bezDeneg) }}</td>
            <td class="col-num">{{ row.ordersCount }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="rows.length" class="totals-bar">
      <div class="totals-title">
        Итого по отмеченным
        <span class="totals-count">({{ selectedIds.length }})</span>
      </div>
      <div class="totals-line">
        {{ totalsPreview }}
      </div>
      <div class="totals-grid">
        <div class="totals-item cash">
          <span class="totals-item-label">Наличными</span>
          <span class="totals-item-value">{{ formatMoney(selectedTotals.cash) }} руб.</span>
        </div>
        <div class="totals-item total">
          <span class="totals-item-label">Общая сумма</span>
          <span class="totals-item-value">{{ formatMoney(selectedTotals.total) }} руб.</span>
        </div>
        <div class="totals-item bez">
          <span class="totals-item-label">Без денег</span>
          <span class="totals-item-value">{{ formatMoney(selectedTotals.bezDeneg) }} руб.</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ORDERS_API_BASE } from '@/config/api';
import axios from 'axios';

const API_URL = ORDERS_API_BASE + '/kassa_30days.php';

export default {
  name: 'Kassa30DaysPage',
  data() {
    return {
      days: 30,
      loading: false,
      loaded: false,
      error: null,
      rows: [],
      selectedIds: [],
      dateFrom: '',
      dateTo: '',
    };
  },
  computed: {
    periodLabel() {
      if (!this.dateFrom || !this.dateTo) return '';
      const fmt = (ymd) => {
        if (!ymd || ymd.length < 10) return ymd;
        const [y, m, d] = ymd.split('-');
        return `${d}.${m}.${y}`;
      };
      return `с ${fmt(this.dateFrom)} по ${fmt(this.dateTo)}`;
    },
    allSelected() {
      return this.rows.length > 0 && this.selectedIds.length === this.rows.length;
    },
    someSelected() {
      return this.selectedIds.length > 0;
    },
    selectedTotals() {
      const set = new Set(this.selectedIds);
      let cash = 0;
      let total = 0;
      let bezDeneg = 0;
      this.rows.forEach((row) => {
        if (!set.has(row.id)) return;
        cash += Number(row.cash) || 0;
        total += Number(row.total) || 0;
        bezDeneg += Number(row.bezDeneg) || 0;
      });
      return {
        cash: Math.round(cash * 100) / 100,
        total: Math.round(total * 100) / 100,
        bezDeneg: Math.round(bezDeneg * 100) / 100,
      };
    },
    totalsPreview() {
      if (!this.selectedIds.length) {
        return 'Отметьте кассы чекбоксами — итог посчитается автоматически.';
      }
      const t = this.selectedTotals;
      return `Наличными: ${this.formatMoney(t.cash)} руб.  Общая сумма: ${this.formatMoney(t.total)} руб.  Без денег: ${this.formatMoney(t.bezDeneg)} руб.`;
    },
  },
  mounted() {
    this.load(false);
  },
  methods: {
    formatMoney(value) {
      const n = Number(value);
      if (!isFinite(n)) return '0,00';
      return n.toLocaleString('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    isSelected(id) {
      return this.selectedIds.indexOf(id) !== -1;
    },
    toggleRow(id) {
      const idx = this.selectedIds.indexOf(id);
      if (idx === -1) {
        this.selectedIds = this.selectedIds.concat([id]);
      } else {
        this.selectedIds = this.selectedIds.filter((x) => x !== id);
      }
    },
    toggleAll(checked) {
      if (checked) {
        this.selectedIds = this.rows.map((r) => r.id);
      } else {
        this.selectedIds = [];
      }
    },
    async load(refresh) {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get(API_URL, {
          params: {
            days: this.days,
            refresh: refresh ? 1 : 0,
          },
          timeout: 120000,
        });
        if (!data || !data.success) {
          this.error = (data && data.message) || 'Не удалось загрузить кассы';
          this.rows = [];
          this.selectedIds = [];
          this.loaded = true;
          return;
        }
        this.rows = Array.isArray(data.rows) ? data.rows : [];
        this.dateFrom = data.dateFrom || '';
        this.dateTo = data.dateTo || '';
        this.days = data.days || this.days;
        // сохраняем уже отмеченные id, если они ещё есть в ответе
        const idSet = new Set(this.rows.map((r) => r.id));
        this.selectedIds = this.selectedIds.filter((id) => idSet.has(id));
        this.loaded = true;
      } catch (e) {
        this.error = (e && e.message) || 'Ошибка загрузки';
        this.rows = [];
        this.loaded = true;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.kassa-30days {
  padding: 16px 20px 40px;
  max-width: 960px;
}

.header-main {
  margin-bottom: 16px;
}

.header-main__title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
}

.controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.btn-load {
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  background: #005eeb;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
}

.btn-load:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.period-label,
.rows-count {
  color: #555;
  font-size: 14px;
}

.error-msg {
  width: 100%;
  margin: 0;
  color: #c00;
}

.empty-msg {
  color: #666;
}

.table-wrap {
  overflow-x: auto;
  margin-bottom: 16px;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.report-table th,
.report-table td {
  border: 1px solid #ddd;
  padding: 8px 10px;
  text-align: left;
  vertical-align: middle;
}

.report-table th {
  background: #f5f5f5;
  font-weight: 600;
}

.report-table tbody tr {
  cursor: pointer;
}

.report-table tbody tr:hover {
  background: #f7faff;
}

.report-table tbody tr.selected {
  background: #eaf2ff;
}

.col-check {
  width: 42px;
  text-align: center;
}

.col-num {
  text-align: right;
  white-space: nowrap;
}

.cell-label {
  font-weight: 600;
  white-space: nowrap;
}

.totals-bar {
  position: sticky;
  bottom: 0;
  background: #fff;
  border: 1px solid #d8e2f0;
  border-radius: 8px;
  padding: 14px 16px;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.06);
}

.totals-title {
  font-weight: 700;
  margin-bottom: 6px;
}

.totals-count {
  font-weight: 500;
  color: #666;
}

.totals-line {
  margin-bottom: 12px;
  font-size: 14px;
  color: #222;
}

.totals-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.totals-item {
  border-radius: 6px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.totals-item.cash {
  background: #e8f5e9;
}

.totals-item.total {
  background: #fff8e1;
}

.totals-item.bez {
  background: #e3f2fd;
}

.totals-item-label {
  font-size: 12px;
  color: #555;
}

.totals-item-value {
  font-size: 16px;
  font-weight: 700;
}

@media (max-width: 700px) {
  .totals-grid {
    grid-template-columns: 1fr;
  }
}
</style>
